import { addDays, addTally, noTally, rate, weekdayOf, REASONS, type Reason, type Tally } from '@day';
import { useStore } from '../app/store';
import { openCourier, ui } from '../app/ui';
import { dayWords, money } from '../app/words';
import { track } from '../data/analytics';
import type { Model } from '../data/model';
import { reportCsv, reportFileName } from '../data/reportCsv';
import { Av } from '../ui/bits';
import { Icon } from '../ui/Icon';
import { courierTally } from './Couriers';

/**
 * Reports: the numbers row, then the chart and what's behind it (control-room-web.html, "Reports"). Every figure is
 * counted as the phone's stats count it (`CourierStats`): a stop on the day it was handled, first time without an
 * earlier visit, on time by its deliver-by.
 */

const pct = (n: number, of: number) => {
  const r = rate(n, of);
  return r == null ? '–' : `${Math.round(r * 100)}%`;
};

/** A small line of a series, drawn to fill its box. */
function Spark({ vals, color }: { vals: (number | null)[]; color: string }) {
  const xs = vals.map((v) => v ?? 0);
  const max = Math.max(...xs);
  const min = Math.min(...xs);
  const pts = xs.map((v, i) => `${((i / Math.max(1, xs.length - 1)) * 100).toFixed(1)},${(30 - ((v - min) / (max - min || 1)) * 26).toFixed(1)}`).join(' ');
  return (
    <svg viewBox="0 0 100 32" preserveAspectRatio="none" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
    </svg>
  );
}

/** The period's numbers as a CSV: a row per day and a row per courier, no addresses or customers. */
export function downloadReport(m: Model, R: number) {
  const days = Array.from({ length: R }, (_, i) => addDays(m.today, i - R + 1));
  const text = reportCsv({
    days: days.map((day) => ({ day, tally: m.tallies.get(day)?.all ?? noTally() })),
    couriers: m.people.map((p) => ({ name: p.name, tally: courierTally(m, p.uid, R) })),
    currency: m.currency,
  });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  a.download = reportFileName(m.business?.name, days[0], m.today);
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  track('analytics_downloaded', { days: R, couriers: m.people.length });
}

export function ReportsPage({ m }: { m: Model }) {
  const s = useStore(ui);
  const R = s.range;
  const days = Array.from({ length: R }, (_, i) => addDays(m.today, i - R + 1));
  const before = Array.from({ length: R }, (_, i) => addDays(m.today, i - 2 * R + 1));
  const of = (day: string) => m.tallies.get(day)?.all ?? noTally();
  const sum = (ds: string[]) => ds.reduce((t, d) => addTally(t, of(d)), noTally());
  const cur = sum(days);
  const prev = sum(before);
  const diff = cur.delivered - prev.delivered;
  const working = days.filter((d) => of(d).delivered + of(d).notDelivered > 0).length || 1;
  const loading = m.history == null;
  const H = 180;
  // A line every 10, 20, 50, 100… so a busy business still gets eight or fewer, not a column of numbers.
  const top = Math.max(...days.map((d) => of(d).delivered));
  const step = [10, 20, 50, 100, 200, 500, 1000, 2000, 5000].find((x) => top <= x * 8) ?? 10000;
  const max = Math.max(step, Math.ceil(top / step) * step);
  const grid = Array.from({ length: max / step + 1 }, (_, i) => i * step);
  const reasons = (Object.entries(cur.reasons) as [Reason, number][]).sort((a, b) => b[1] - a[1]);
  const pays: [string, number][] = [
    ['Cash', cur.cash],
    ['Transfer', cur.transfer],
    ['POS', cur.pos],
  ];
  const paid = cur.cash + cur.transfer + cur.pos;
  const range = `${dayWords(days[0], { weekday: false })} – ${dayWords(m.today, { weekday: false })}`;
  const sundaysOff = days.filter((d) => weekdayOf(d) === 0).every((d) => of(d).delivered === 0);
  const peopleWith = m.people.filter((p) => days.some((d) => m.tallies.get(d)?.byCourier.get(p.uid)));
  return (
    <div className="tpage" id="reportsPage">
      <div className="filters">
        <div className="seg" role="group" aria-label="Days">
          {([7, 14, 30] as const).map((n) => (
            <button key={n} aria-pressed={R === n} onClick={() => ui.set({ range: n })}>
              {n} days
            </button>
          ))}
        </div>
        <span className="cap">{range}</span>
        <span className="grow" />
        {loading && <span className="cap">Reading the past days…</span>}
        <button className="btn sm" disabled={loading} data-tip={`These ${R} days as a CSV: a row per day and a row per courier. Numbers only, no addresses or customers`} onClick={() => downloadReport(m, R)}>
          <Icon name="downloadSimple" size="sm" />
          Download
        </button>
      </div>
      <div className="kpis">
        <div className="kpi">
          <div className="eyebrow">Delivered</div>
          <div className="v">{cur.delivered}</div>
          <div className="cap">
            {diff >= 0 ? <span className="pos">{diff} more</span> : <span className="neg">{-diff} fewer</span>} than the {R} days before
          </div>
          <Spark vals={days.map((d) => of(d).delivered)} color="var(--ink)" />
        </div>
        <div className="kpi">
          <div className="eyebrow">
            <span className="dotted" data-tip="Delivered the first time a courier went. A closed shop, or a customer who asked for another day, isn’t counted.">
              Delivered first time
            </span>
          </div>
          <div className="v">{pct(cur.firstTime, cur.rated)}</div>
          <div className="cap">
            {pct(prev.firstTime, prev.rated)} the {R} days before
          </div>
          <Spark vals={days.map((d) => rate(of(d).firstTime, of(d).rated))} color="var(--ink)" />
        </div>
        <div className="kpi">
          <div className="eyebrow">
            <span className="dotted" data-tip="Delivered by the deliver-by time, of the stops that had one">On time</span>
          </div>
          <div className="v">{pct(cur.onTimeMet, cur.onTimeOf)}</div>
          <div className="cap">
            {pct(prev.onTimeMet, prev.onTimeOf)} the {R} days before
          </div>
          <Spark vals={days.map((d) => rate(of(d).onTimeMet, of(d).onTimeOf))} color="var(--ink)" />
        </div>
        <div className="kpi">
          <div className="eyebrow">Not delivered</div>
          <div className="v">{cur.notDelivered}</div>
          <div className="cap">{reasons.length ? `Mostly ${REASONS[reasons[0][0]].toLowerCase()}` : 'None'}</div>
          <Spark vals={days.map((d) => of(d).notDelivered)} color="var(--negative)" />
        </div>
        <div className="kpi">
          <div className="eyebrow">Collected at the door</div>
          <div className="v">{money(cur.collected, m.currency)}</div>
          <div className="cap">{m.handInTotal ? `${money(m.handInTotal, m.currency)} still to hand in` : 'Nothing waiting to hand in'}</div>
          <Spark vals={days.map((d) => of(d).collected)} color="var(--ink)" />
        </div>
      </div>
      <div className="cols2">
        <div className="card" style={{ padding: '18px 20px' }}>
          <div className="row">
            <b className="grow">
              {cur.delivered} delivered · about {Math.round(cur.delivered / working)} a working day
            </b>
            <div className="seg" role="group" aria-label="Couriers">
              <button aria-pressed={!s.split} onClick={() => ui.set({ split: false })}>
                All couriers
              </button>
              <button aria-pressed={s.split} onClick={() => ui.set({ split: true })}>
                Each courier
              </button>
            </div>
          </div>
          {s.split && (
            <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
              {peopleWith.map((p) => (
                <span key={p.uid} className="chip">
                  <Av p={p} size="xs" />
                  {p.first}
                </span>
              ))}
            </div>
          )}
          <div className="chart">
            <div className="y">
              {grid.map((g) => (
                <span key={g} style={{ bottom: (g / max) * H }}>
                  {g}
                </span>
              ))}
            </div>
            <div className="plot" style={{ height: H }}>
              {grid.map((g) => (
                <div key={g} className="gl" style={{ bottom: (g / max) * H }} />
              ))}
              {days.map((d) => {
                const t = of(d);
                const wk = m.tallies.get(addDays(d, -7))?.all;
                const today = d === m.today;
                const words = `${dayWords(d)}: ${t.delivered} delivered${today ? ' so far' : ''}${
                  wk && t.delivered && wk.delivered ? ` · ${t.delivered === wk.delivered ? 'the same as' : `${Math.abs(t.delivered - wk.delivered)} ${t.delivered > wk.delivered ? 'more' : 'fewer'} than`} ${dayWords(addDays(d, -7)).split(' ').slice(0, 2).join(' ')}` : ''
                }`;
                return (
                  <div key={d} className={`col${today ? ' today' : ''}`} data-tip={words} data-side="top">
                    {s.split
                      ? peopleWith.map((p) => {
                          const n = m.tallies.get(d)?.byCourier.get(p.uid)?.delivered ?? 0;
                          return n ? <i key={p.uid} style={{ height: (n / max) * H, backgroundColor: p.hex }} /> : null;
                        })
                      : t.delivered > 0 && <i style={{ height: (t.delivered / max) * H, backgroundColor: 'var(--ink)' }} />}
                  </div>
                );
              })}
            </div>
            <div />
            <div className="xl">
              {days.map((d, i) => (
                <span key={d}>{R <= 7 ? dayWords(d, { short: true }).split(' ')[0] : R <= 14 || i % 5 === (R - 1) % 5 ? Number(d.slice(8)) : ''}</span>
              ))}
            </div>
          </div>
          <div className="cap" style={{ marginTop: 10 }}>
            Today is hatched while it’s still going.{sundaysOff ? ' No deliveries on Sundays.' : ''}
          </div>
        </div>
        <div className="card">
          <div style={{ padding: '16px 18px 4px' }}>
            <b>By courier</b>
          </div>
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Courier</th>
                  <th className="num">Delivered</th>
                  <th className="num">
                    <span className="dotted" data-tip="Delivered the first time they went">
                      First time
                    </span>
                  </th>
                  <th className="num">On time</th>
                </tr>
              </thead>
              <tbody>
                {m.people.map((p) => {
                  const t: Tally = courierTally(m, p.uid, R);
                  return (
                    <tr key={p.uid} className="r" data-ctx={`courier:${p.uid}`} onClick={() => openCourier(p.uid)}>
                      <td>
                        <span className="row" style={{ gap: 8 }}>
                          <Av p={p} size="sm" />
                          {p.first}
                        </span>
                      </td>
                      <td className="num mono">{t.delivered}</td>
                      <td className="num mono">{pct(t.firstTime, t.rated)}</td>
                      <td className="num mono">{pct(t.onTimeMet, t.onTimeOf)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="cols2" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div className="card" style={{ padding: '16px 20px' }}>
          <b>How customers paid at the door</b>
          {paid ? (
            pays.map(([w, n]) => (
              <div key={w} className="rank">
                <span>{w}</span>
                <div className="bar">
                  <i style={{ width: `${(n / paid) * 100}%`, background: 'var(--ink)' }} />
                </div>
                <span className="mono">
                  {money(n, m.currency)} · {Math.round((n / paid) * 100)}%
                </span>
              </div>
            ))
          ) : (
            <div className="cap" style={{ marginTop: 10 }}>
              Nothing collected at the door in these days.
            </div>
          )}
        </div>
        <div className="card" style={{ padding: '16px 20px' }}>
          <b>Why stops weren’t delivered</b>
          {reasons.length ? (
            reasons.map(([r, n]) => (
              <div key={r} className="rank">
                <span>{REASONS[r]}</span>
                <div className="bar">
                  <i style={{ width: `${(n / cur.notDelivered) * 100}%`, background: r === 'closed' || r === 'anotherDay' ? 'var(--caution)' : 'var(--negative)' }} />
                </div>
                <span className="mono">
                  {n} · {Math.round((n / cur.notDelivered) * 100)}%
                </span>
              </div>
            ))
          ) : (
            <div className="cap" style={{ marginTop: 10 }}>
              Every stop delivered in these days.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
